import { useState, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/AppShell";
import Overview from "@/pages/Overview";
import Dealers from "@/pages/Dealers";
import DealerDetail from "@/pages/DealerDetail";
import Payments from "@/pages/Payments";
import Invoices from "@/pages/Invoices";
import Alerts from "@/pages/Alerts";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function App() {
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem("auth_token") || localStorage.getItem("auth_token"); }
    catch { return null; }
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Global axios interceptor - auto logout on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          setToken(null);
          setUser(null);
          try {
            sessionStorage.removeItem("auth_token");
            localStorage.removeItem("auth_token");
          } catch { /* ignore */ }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    if (token) {
      axios
        .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          setUser(res.data);
          setLoading(false);
        })
        .catch(() => {
          setToken(null);
          setUser(null);
          try {
            sessionStorage.removeItem("auth_token");
            localStorage.removeItem("auth_token");
          } catch { /* ignore */ }
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = useCallback((newToken, email) => {
    setToken(newToken);
    setUser({ email });
    try {
      sessionStorage.setItem("auth_token", newToken);
      localStorage.setItem("auth_token", newToken);
    } catch { /* ignore */ }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    try {
      sessionStorage.removeItem("auth_token");
      localStorage.removeItem("auth_token");
    } catch { /* ignore */ }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ token, user, login, logout }}>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={!token ? <Login /> : <Navigate to="/" />} />
          <Route
            path="/*"
            element={
              token ? (
                <AppShell>
                  <Routes>
                    <Route path="/" element={<Overview />} />
                    <Route path="/dealers" element={<Dealers />} />
                    <Route path="/dealers/:dealerId" element={<DealerDetail />} />
                    <Route path="/payments" element={<Payments />} />
                    <Route path="/invoices" element={<Invoices />} />
                    <Route path="/alerts" element={<Alerts />} />
                    <Route path="/settings" element={<Settings />} />
                  </Routes>
                </AppShell>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
